import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { trialUpdateService } from '../../services/trialUpdateService';
import { toast } from 'react-hot-toast';
import { 
  Search, 
  Filter, 
  Plus, 
  Edit, 
  Eye, 
  Users, 
  MapPin, 
  Calendar,
  CheckCircle,
  Clock,
  AlertCircle,
  X,
  Save,
  UserPlus,
  Trash2,
  FileText
} from 'lucide-react';

const TrialUpdate = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedTrial, setSelectedTrial] = useState(null);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAssignmentDetailModal, setShowAssignmentDetailModal] = useState(false);
  const [showEditAssignmentModal, setShowEditAssignmentModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [viewMode, setViewMode] = useState('grid');
  const [isLoading, setIsLoading] = useState(false);
  const [adminTrials, setAdminTrials] = useState([]);
  const [plotOfficers, setPlotOfficers] = useState([]);
  const [trialAssignments, setTrialAssignments] = useState([]);

  // Load data from API with fallback to mock data
  const loadAdminTrials = async () => {
    try {
      setIsLoading(true);
      const response = await trialUpdateService.getAllTrials();
      if (response.success) {
        setAdminTrials(response.data || response);
      } else {
        throw new Error(response.message || 'Failed to load trials');
      }
    } catch (error) {
      console.warn('API failed, falling back to mock data:', error.message);
      // Fallback to mock data
      setAdminTrials([
    {
      id: 1,
      trialCode: 'ARTRICE2024KHARIFA001',
      trialName: 'Advanced Rice Technology Trial',
      trialType: 'ART',
      crop: 'Rice',
      year: 2024,
      season: 'Kharif',
      status: 'active',
      description: 'Evaluation of advanced rice varieties for yield and disease resistance',
      createdBy: 'Admin',
      createdDate: '2024-01-10',
      locations: [],
      assignedOfficers: []
    },
    {
      id: 2,
      trialCode: 'ARTMAIZE2024KHARIFA002',
      trialName: 'Maize Hybrid Evaluation',
      trialType: 'ART',
      crop: 'Maize',
      year: 2024,
      season: 'Kharif',
      status: 'active',
      description: 'Testing new maize hybrid varieties for drought tolerance',
      createdBy: 'Admin',
      createdDate: '2024-01-08',
      locations: [
        {
          id: 1,
          locationCode: 'TN01COI01BLK01CEN001',
          locationName: 'Coimbatore Research Centre',
          state: 'Tamil Nadu',
          district: 'Coimbatore',
          block: 'Coimbatore Block',
          centre: 'Coimbatore Centre',
          type: 'TNAU',
          plotSize: '100 sq.m',
          soilType: 'Clay Loam',
          assignedOfficer: 'John Doe',
          status: 'active'
        }
      ],
      assignedOfficers: ['John Doe']
    },
    {
      id: 3,
      trialCode: 'MLTSUGARCANE2024KHARIFM001',
      trialName: 'Sugarcane Multi-Location Trial',
      trialType: 'MLT',
      crop: 'Sugarcane',
      year: 2024,
      season: 'Kharif',
      status: 'active',
      description: 'Multi-location trial for sugarcane variety evaluation',
      createdBy: 'Admin',
      createdDate: '2024-01-05',
      locations: [
        {
          id: 1,
          locationCode: 'TN01COI01BLK01CEN001',
          locationName: 'Coimbatore Research Centre',
          state: 'Tamil Nadu',
          district: 'Coimbatore',
          block: 'Coimbatore Block',
          centre: 'Coimbatore Centre',
          type: 'TNAU',
          plotSize: '200 sq.m',
          soilType: 'Clay Loam',
          assignedOfficer: null,
           status: 'active'
        },
        {
          id: 2,
          locationCode: 'TN02MAD01BLK01CEN001',
          locationName: 'Madurai Research Centre',
          state: 'Tamil Nadu',
          district: 'Madurai',
          block: 'Madurai Block',
          centre: 'Madurai Centre',
          type: 'TNAU',
          plotSize: '200 sq.m',
          soilType: 'Clay',
          assignedOfficer: null,
           status: 'active'
        }
      ],
      assignedOfficers: []
    }
      ]);
    }
  };

  // Load plot officers from API with fallback to mock data
  const loadPlotOfficers = async () => {
    try {
      const response = await trialUpdateService.getAvailablePlotOfficers();
      if (response.success) {
        setPlotOfficers(response.data || response);
      } else {
        throw new Error(response.message || 'Failed to load plot officers');
      }
    } catch (error) {
      console.warn('API failed, falling back to mock data:', error.message);
      // Fallback to mock data
      setPlotOfficers([
    { id: 1, name: 'John Doe', email: 'john.doe@tnau.ac.in', phone: '+91 9876543210', status: 'active' },
    { id: 2, name: 'Jane Smith', email: 'jane.smith@tnau.ac.in', phone: '+91 9876543211', status: 'active' },
    { id: 3, name: 'Mike Johnson', email: 'mike.johnson@tnau.ac.in', phone: '+91 9876543212', status: 'active' },
    { id: 4, name: 'Sarah Wilson', email: 'sarah.wilson@tnau.ac.in', phone: '+91 9876543213', status: 'active' }
      ]);
    }
  };

  // Load trial assignments from API with fallback to mock data
  const loadTrialAssignments = async () => {
    try {
      const response = await trialUpdateService.getTrialAssignments(selectedTrial?.id);
      if (response.success) {
        setTrialAssignments(response.data || response);
      } else {
        throw new Error(response.message || 'Failed to load trial assignments');
      }
    } catch (error) {
      console.warn('API failed, falling back to mock data:', error.message);
      // Fallback to mock data
      setTrialAssignments([
    {
      id: 1,
      trialId: 1,
      locationCode: 'ARTRICE2024KHARIFA001001',
      locationName: 'Coimbatore Research Station',
      latitude: '11.0168',
      longitude: '76.9558',
      altitude: '411',
      sfNumber: 'SF001',
      locationAddress: 'Coimbatore, Tamil Nadu',
      assignedOfficer: 'Dr. Rajesh Kumar',
      culture: 'Hybrid Rice Variety',
      treatment: 'T1 - Control',
      replications: 3,
      observations: ['Germination %', 'Plant height', 'Yield'],
      experimentalArea: 2.5,
      plotSize: 25,
      soilType: 'Clay Loam',
      soilTestDate: '2024-01-15',
      soilTestDetails: 'pH: 6.8, N: 280 kg/ha, P: 45 kg/ha, K: 320 kg/ha',
      irrigationSource: 'Canal Irrigation',
      previousCrop: 'Paddy',
      seedTreatment: 'Thiram @ 2g/kg seed',
      ploughing: '2 times disc ploughing',
      sowingDate: '2024-06-15',
      plantingDate: '2024-07-01',
      basalOrganic: 'Farm Yard Manure',
      basalOrganicDate: '2024-06-10',
      basalOrganicQuantity: 5000,
      basalFertilizerDate: '2024-06-12',
      basalFertilizer: 'NPK 20:20:20',
      basalFertilizerQuantity: 120,
      topDressingDate: '2024-07-15',
      topDressing: 'Urea',
      topDressingQuantity: 60,
      weedManagement: 'Hand weeding on 20th and 40th day after sowing',
      plantProtection: 'Chlorpyriphos @ 2ml/lit on 30th day',
      harvestDate: '2024-10-15',
      monitoringVisitDate: '2024-08-15',
      monitoringComments: 'Crop growth is satisfactory, no pest incidence observed',
      plotOfficerVisitDate: '2024-08-20',
      plotOfficerComments: 'All replications showing uniform growth',
      inspectionVisitDate: '2024-09-01',
      inspectionComments: 'Trial is progressing well, recommend continuation',
      fieldDayDate: '2024-09-15',
      farmersFeedback: 'Farmers are satisfied with the variety performance'
    },
    {
      id: 2,
      trialId: 2,
      locationCode: 'ARTMAIZE2024KHARIFA002001',
      locationName: 'Madurai Research Station',
      latitude: '9.9252',
      longitude: '78.1198',
      altitude: '134',
      sfNumber: 'SF002',
      locationAddress: 'Madurai, Tamil Nadu',
      assignedOfficer: 'Dr. Priya Sharma',
      culture: 'Maize Hybrid',
      treatment: 'T1 - Drought Tolerant',
      replications: 3,
      observations: ['Germination %', 'Plant height', 'Cob length', 'Yield'],
      experimentalArea: 3.0,
      plotSize: 30,
      soilType: 'Clay',
      soilTestDate: '2024-01-20',
      soilTestDetails: 'pH: 7.2, N: 250 kg/ha, P: 40 kg/ha, K: 280 kg/ha',
      irrigationSource: 'Borewell',
      previousCrop: 'Maize',
      seedTreatment: 'Imidacloprid @ 3g/kg seed',
      ploughing: '3 times disc ploughing',
      sowingDate: '2024-06-20',
      plantingDate: '2024-07-05',
      basalOrganic: 'Vermicompost',
      basalOrganicDate: '2024-06-15',
      basalOrganicQuantity: 3000,
      basalFertilizerDate: '2024-06-18',
      basalFertilizer: 'DAP',
      basalFertilizerQuantity: 100,
      topDressingDate: '2024-07-25',
      topDressing: 'Urea',
      topDressingQuantity: 80,
      weedManagement: 'Atrazine @ 1kg/ha pre-emergence',
      plantProtection: 'Carbendazim @ 1g/lit for leaf blight',
      harvestDate: '2024-10-25',
      monitoringVisitDate: '2024-08-25',
      monitoringComments: 'Good drought tolerance observed',
      plotOfficerVisitDate: '2024-08-30',
      plotOfficerComments: 'Uniform stand establishment across replications',
      inspectionVisitDate: '2024-09-10',
      inspectionComments: 'Promising results for drought tolerance',
      fieldDayDate: '2024-09-25',
      farmersFeedback: 'Farmers interested in adopting this variety'
    }
      ]);
    }
  };

  // Mock data arrays for dropdowns
  const observations = [
    'Germination %', 'Plant height', 'Yield', 'Cob length', 'Grain weight',
    'Disease incidence', 'Pest incidence', 'Days to flowering', 'Days to maturity'
  ];

  const soilTypes = [
    'Clay Loam', 'Clay', 'Sandy Loam', 'Sandy Clay', 'Loam', 'Silt Loam'
  ];

  const irrigationSources = [
    'Canal Irrigation', 'Borewell', 'Open Well', 'Tank Irrigation', 'Drip Irrigation', 'Sprinkler'
  ];

  const officers = [
    'Dr. Rajesh Kumar', 'Dr. Priya Sharma', 'Dr. Amit Patel', 'Dr. Sneha Reddy',
    'Mr. Ramesh Kumar', 'Ms. Lakshmi Devi', 'Mr. Suresh Babu', 'Ms. Geetha Rani'
  ];

  const years = ['2024', '2025', '2026'];
  const seasons = ['Kharif', 'Rabi', 'Winter', 'Summer'];

  // Load data on component mount
  useEffect(() => {
    loadAdminTrials();
    loadPlotOfficers();
  }, []);

  // Load trial assignments when selected trial changes
  useEffect(() => {
    if (selectedTrial) {
      loadTrialAssignments();
    }
  }, [selectedTrial]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'pending_location': return 'text-yellow-600 bg-yellow-100';
      case 'pending_assignment': return 'text-orange-600 bg-orange-100';
      case 'completed': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircle className="w-4 h-4" />;
      case 'pending_location': return <MapPin className="w-4 h-4" />;
      case 'pending_assignment': return <Users className="w-4 h-4" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const filteredTrials = adminTrials.filter(trial => {
    const matchesSearch = trial.trialName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         trial.trialCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         trial.crop.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || trial.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const handleAddLocation = async (trial) => {
    try {
    setSelectedTrial(trial);
    setShowLocationModal(true);
      // Load available plot officers for this trial
      await loadPlotOfficers();
    } catch (error) {
      console.error('Error loading plot officers:', error);
      toast.error('Failed to load plot officers');
    }
  };

  const handleAssignOfficer = async (trial) => {
    try {
    setSelectedTrial(trial);
    setShowAssignmentModal(true);
      // Load available plot officers for this trial
      await loadPlotOfficers();
    } catch (error) {
      console.error('Error loading plot officers:', error);
      toast.error('Failed to load plot officers');
    }
  };

  const handleViewDetails = (trial) => {
    setSelectedTrial(trial);
    setShowViewModal(true);
  };

  const openAssignmentDetailModal = (assignment) => {
    setSelectedAssignment(assignment);
    setShowAssignmentDetailModal(true);
  };

  const openEditAssignmentModal = (assignment) => {
    setEditingAssignment(assignment);
    setShowEditAssignmentModal(true);
  };

  const generateLocationCode = (trialCode) => {
    return `${trialCode}001`;
  };

  // Handle officer assignment to trial location
  const handleOfficerAssignment = async (assignmentData) => {
    try {
      setIsLoading(true);
      const response = await trialUpdateService.assignOfficerToLocation(
        selectedTrial.id,
        assignmentData.locationId,
        assignmentData
      );
      
      if (response.success) {
        toast.success('Officer assigned successfully!');
        setShowAssignmentModal(false);
        // Reload trial assignments to reflect the change
        await loadTrialAssignments();
        // Reload admin trials to update the status
        await loadAdminTrials();
      } else {
        throw new Error(response.message || 'Failed to assign officer');
      }
    } catch (error) {
      console.error('Error assigning officer:', error);
      toast.error(error.message || 'Failed to assign officer');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle trial status update
  const handleTrialStatusUpdate = async (trialId, newStatus) => {
    try {
      setIsLoading(true);
      const response = await trialUpdateService.updateTrialStatus(trialId, newStatus);
      
      if (response.success) {
        toast.success('Trial status updated successfully!');
        // Reload admin trials to reflect the change
        await loadAdminTrials();
      } else {
        throw new Error(response.message || 'Failed to update trial status');
      }
    } catch (error) {
      console.error('Error updating trial status:', error);
      toast.error(error.message || 'Failed to update trial status');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle trial assignment update
  const handleAssignmentUpdate = async (assignmentId, assignmentData) => {
    try {
      setIsLoading(true);
      const response = await trialUpdateService.updateTrialAssignment(assignmentId, assignmentData);
      
      if (response.success) {
        toast.success('Assignment updated successfully!');
        setShowEditAssignmentModal(false);
        setEditingAssignment(null);
        // Reload trial assignments to reflect the change
        await loadTrialAssignments();
      } else {
        throw new Error(response.message || 'Failed to update assignment');
      }
    } catch (error) {
      console.error('Error updating assignment:', error);
      toast.error(error.message || 'Failed to update assignment');
    } finally {
      setIsLoading(false);
    }
  };

  // Export trial data
  const handleExportTrialData = async (trialId, format = 'excel') => {
    try {
      setIsLoading(true);
      const response = await trialUpdateService.exportTrialData(trialId, format);
      
      // Create download link for the exported file
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `trial-${trialId}-data.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Trial data exported successfully!');
    } catch (error) {
      console.error('Error exporting trial data:', error);
      toast.error('Failed to export trial data');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle adding new location to trial
  const handleAddLocationToTrial = async (locationData) => {
    try {
      setIsLoading(true);
      // This would typically call an API to add a location to a trial
      // For now, we'll simulate the process and update local state
      const newLocation = {
        id: Date.now(), // Generate temporary ID
        ...locationData,
        status: 'active',
        assignedOfficer: null
      };
      
      // Update the selected trial with the new location
      const updatedTrial = {
        ...selectedTrial,
        locations: [...(selectedTrial.locations || []), newLocation]
      };
      
      // Update the adminTrials state
      setAdminTrials(prevTrials => 
        prevTrials.map(trial => 
          trial.id === selectedTrial.id ? updatedTrial : trial
        )
      );
      
      toast.success('Location added to trial successfully!');
      setShowLocationModal(false);
      setSelectedTrial(updatedTrial);
    } catch (error) {
      console.error('Error adding location to trial:', error);
      toast.error('Failed to add location to trial');
    } finally {
      setIsLoading(false);
    }
  };

  // Get trial progress data
  const getTrialProgress = async (trialId) => {
    try {
      const response = await trialUpdateService.getTrialProgress(trialId);
      if (response.success) {
        return response.data || response;
      } else {
        throw new Error(response.message || 'Failed to fetch trial progress');
      }
    } catch (error) {
      console.warn('API failed to fetch trial progress:', error.message);
      // Return mock progress data as fallback
      return {
        trialId,
        progress: 75,
        milestones: [
          { name: 'Land Preparation', status: 'completed', date: '2024-06-01' },
          { name: 'Sowing', status: 'completed', date: '2024-06-15' },
          { name: 'Germination', status: 'completed', date: '2024-06-25' },
          { name: 'Vegetative Growth', status: 'in-progress', date: '2024-07-15' },
          { name: 'Flowering', status: 'pending', date: null },
          { name: 'Harvest', status: 'pending', date: null }
        ],
        lastUpdated: '2024-07-15'
      };
    }
  };

  // Handle assignment form submission
  const handleAssignmentFormSubmit = async (e) => {
    e.preventDefault();
    
    const formData = new FormData(e.target);
    const assignmentData = {
      culture: formData.get('culture'),
      treatment: formData.get('treatment'),
      replications: parseInt(formData.get('replications')),
      observations: formData.getAll('observations'),
      locationCode: formData.get('locationCode'),
      locationName: formData.get('locationName'),
      latitude: formData.get('latitude'),
      longitude: formData.get('longitude'),
      altitude: formData.get('altitude'),
      sfNumber: formData.get('sfNumber'),
      locationAddress: formData.get('locationAddress'),
      assignedOfficer: formData.get('assignedOfficer'),
      experimentalArea: parseFloat(formData.get('experimentalArea')),
      plotSize: parseFloat(formData.get('plotSize')),
      soilType: formData.get('soilType'),
      irrigationSource: formData.get('irrigationSource'),
      previousCrop: formData.get('previousCrop'),
      seedTreatment: formData.get('seedTreatment'),
      ploughing: formData.get('ploughing'),
      sowingDate: formData.get('sowingDate'),
      plantingDate: formData.get('plantingDate')
    };

    try {
      await handleOfficerAssignment(assignmentData);
    } catch (error) {
      console.error('Error submitting assignment:', error);
      toast.error('Failed to submit assignment');
    }
  };

  const getTrialAssignments = (trialId) => {
    return trialAssignments.filter(assignment => assignment.trialId === trialId);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Trial Update</h1>
          <p className="text-gray-600">Manage trials created by admin and assign locations & officers</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600'}`}
          >
            <div className="w-4 h-4 grid grid-cols-2 gap-0.5">
              <div className="bg-current rounded-sm"></div>
              <div className="bg-current rounded-sm"></div>
              <div className="bg-current rounded-sm"></div>
              <div className="bg-current rounded-sm"></div>
            </div>
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600'}`}
          >
            <div className="w-4 h-4 space-y-1">
              <div className="bg-current rounded-sm h-0.5"></div>
              <div className="bg-current rounded-sm h-0.5"></div>
              <div className="bg-current rounded-sm h-0.5"></div>
            </div>
          </button>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search trials by name, code, or crop..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div className="flex gap-2">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          >
            <option value="all">All Status</option>
            <option value="pending_location">Pending Location</option>
            <option value="pending_assignment">Pending Assignment</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
          </select>
        </div>
      </div>

      {/* Trials Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTrials.map((trial) => (
            <div key={trial.id} className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{trial.trialName}</h3>
                    <p className="text-sm text-gray-600 mb-2">{trial.trialCode}</p>
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(trial.status)}
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(trial.status)}`}>
                        {trial.status.replace('_', ' ')}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Crop:</span>
                    <span className="font-medium">{trial.crop}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Season:</span>
                    <span className="font-medium">{trial.season}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Locations:</span>
                    <span className="font-medium">{trial.locations.length}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Officers:</span>
                    <span className="font-medium">{trial.assignedOfficers.length}</span>
                  </div>
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={() => handleViewDetails(trial)}
                    className="flex-1 px-3 py-2 text-sm text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg flex items-center justify-center space-x-1"
                  >
                    <Eye className="w-4 h-4" />
                    <span>View</span>
                  </button>
                  {trial.status === 'pending_location' && (
                    <button
                      onClick={() => handleAddLocation(trial)}
                      className="flex-1 px-3 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center justify-center space-x-1"
                    >
                      <MapPin className="w-4 h-4" />
                      <span>Add Location</span>
                    </button>
                  )}
                  {trial.status === 'pending_assignment' && (
                    <button
                      onClick={() => handleAssignOfficer(trial)}
                      className="flex-1 px-3 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center justify-center space-x-1"
                    >
                      <UserPlus className="w-4 h-4" />
                      <span>Assign Officer</span>
                    </button>
                  )}
                  {trial.status === 'active' && (
                    <button
                      onClick={() => handleViewDetails(trial)}
                      className="flex-1 px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center space-x-1"
                    >
                      <FileText className="w-4 h-4" />
                      <span>View Assignments</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trial</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Crop</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Locations</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Officers</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTrials.map((trial) => (
                  <tr key={trial.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{trial.trialName}</div>
                        <div className="text-sm text-gray-500">{trial.trialCode}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{trial.crop}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(trial.status)}`}>
                        {trial.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{trial.locations.length}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{trial.assignedOfficers.length}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleViewDetails(trial)}
                          className="text-gray-600 hover:text-primary-600"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {trial.status === 'pending_location' && (
                          <button
                            onClick={() => handleAddLocation(trial)}
                            className="text-primary-600 hover:text-primary-700"
                          >
                            <MapPin className="w-4 h-4" />
                          </button>
                        )}
                                                 {trial.status === 'pending_assignment' && (
                           <button
                             onClick={() => handleAssignOfficer(trial)}
                             className="text-primary-600 hover:text-primary-700"
                           >
                             <UserPlus className="w-4 h-4" />
                           </button>
                         )}
                         {trial.status === 'active' && (
                           <button
                             onClick={() => handleViewDetails(trial)}
                             className="text-green-600 hover:text-green-700"
                           >
                             <FileText className="w-4 h-4" />
                           </button>
                         )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Location Assignment Modal */}
      {showLocationModal && selectedTrial && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">Add Location Details</h2>
                <button
                  onClick={() => setShowLocationModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-1">Trial: {selectedTrial.trialName}</p>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                      <option>Tamil Nadu</option>
                      <option>Karnataka</option>
                      <option>Kerala</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">District</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                      <option>Coimbatore</option>
                      <option>Madurai</option>
                      <option>Salem</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Block</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                      <option>Coimbatore Block</option>
                      <option>Madurai Block</option>
                      <option>Salem Block</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Centre</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                      <option>Coimbatore Centre</option>
                      <option>Madurai Centre</option>
                      <option>Salem Centre</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Plot Size (sq.m)</label>
                    <input
                      type="number"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      placeholder="100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Soil Type</label>
                    <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                      <option>Clay Loam</option>
                      <option>Clay</option>
                      <option>Sandy Loam</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowLocationModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center space-x-2">
                <Save className="w-4 h-4" />
                <span>Add Location</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Officer Assignment Modal */}
      {showAssignmentModal && selectedTrial && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-gray-900">Assign Plot Officers</h2>
                <button
                  onClick={() => setShowAssignmentModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <p className="text-sm text-gray-600 mt-1">Trial: {selectedTrial.trialName}</p>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Available Locations</h3>
                {selectedTrial.locations.map((location) => (
                  <div key={location.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{location.locationName}</h4>
                        <p className="text-sm text-gray-600">{location.locationCode}</p>
                        <p className="text-sm text-gray-600">{location.state} - {location.district}</p>
                      </div>
                      <div className="ml-4">
                        <select className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                          <option value="">Select Officer</option>
                          {plotOfficers.map((officer) => (
                            <option key={officer.id} value={officer.id}>
                              {officer.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowAssignmentModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center space-x-2">
                <UserPlus className="w-4 h-4" />
                <span>Assign Officers</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comprehensive Trial Assignment Modal */}
      {showAssignmentModal && selectedTrial && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Assign Trial to Location: {selectedTrial.trialName}
              </h2>
              <form onSubmit={handleAssignmentFormSubmit} className="space-y-6">
                {/* Trial Details Section */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="text-md font-semibold text-blue-900 mb-3">Trial Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Culture</label>
                      <input
                        type="text"
                        name="culture"
                        placeholder="e.g., Hybrid Rice Variety"
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Treatment</label>
                      <input
                        type="text"
                        name="treatment"
                        placeholder="e.g., T1 - Control"
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Number of Replications</label>
                      <input
                        type="number"
                        name="replications"
                        min="1"
                        max="10"
                        placeholder="3"
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Select Observations</label>
                      <select name="observations" multiple className="input-field">
                        {observations.map(obs => (
                          <option key={obs} value={obs}>{obs}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Location Details Section */}
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="text-md font-semibold text-green-900 mb-3">Location Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Location Code</label>
                      <input
                        type="text"
                        name="locationCode"
                        value={generateLocationCode(selectedTrial.trialCode)}
                        className="input-field bg-gray-50 font-mono"
                        readOnly
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Location Name</label>
                      <input
                        type="text"
                        name="locationName"
                        placeholder="e.g., Coimbatore Research Station"
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
                      <input
                        type="text"
                        name="latitude"
                        placeholder="11.0168"
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
                      <input
                        type="text"
                        placeholder="76.9558"
                        name="longitude"
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Altitude (m)</label>
                      <input
                        type="text"
                        name="altitude"
                        placeholder="411"
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">SF Number</label>
                      <input
                        type="text"
                        name="sfNumber"
                        placeholder="SF001"
                        className="input-field"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Location Address</label>
                      <textarea
                        name="locationAddress"
                        placeholder="Complete address of the location"
                        rows="2"
                        className="input-field"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Assign Officer</label>
                      <select name="assignedOfficer" className="input-field">
                        <option value="">Select Officer</option>
                        {plotOfficers.map(officer => (
                          <option key={officer.id} value={officer.name}>{officer.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Additional Trial Parameters Section */}
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <h3 className="text-md font-semibold text-yellow-900 mb-3">Additional Parameters</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Experimental Area (ha)</label>
                      <input
                        type="number"
                        name="experimentalArea"
                        step="0.1"
                        min="0.1"
                        placeholder="2.5"
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Plot Size (m²)</label>
                      <input
                        type="number"
                        name="plotSize"
                        step="1"
                        min="1"
                        placeholder="25"
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Soil Type</label>
                      <select name="soilType" className="input-field">
                        <option value="">Select Soil Type</option>
                        {soilTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Irrigation Source</label>
                      <select name="irrigationSource" className="input-field">
                        <option value="">Select Irrigation Source</option>
                        {irrigationSources.map(source => (
                          <option key={source} value={source}>{source}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Previous Crop</label>
                      <input
                        type="text"
                        name="previousCrop"
                        placeholder="e.g., Paddy"
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Seed Treatment</label>
                      <input
                        type="text"
                        name="seedTreatment"
                        placeholder="e.g., Thiram @ 2g/kg seed"
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Ploughing</label>
                      <input
                        type="text"
                        name="ploughing"
                        placeholder="e.g., 2 times disc ploughing"
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Sowing Date</label>
                      <input
                        type="date"
                        name="sowingDate"
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Planting Date</label>
                      <input
                        type="date"
                        name="plantingDate"
                        className="input-field"
                      />
                    </div>
                  </div>
                </div>



                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAssignmentModal(false)}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                  >
                    Assign Trial
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Trial Assignments Modal */}
      {showViewModal && selectedTrial && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Trial Assignments: {selectedTrial.trialName}
              </h2>
              <div className="space-y-4">
                {getTrialAssignments(selectedTrial.id).map((assignment) => (
                  <div key={assignment.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{assignment.locationName}</h3>
                        <p className="text-sm text-gray-600">Location Code: {assignment.locationCode}</p>
                      </div>
                      <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded-full">
                        Active
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Assigned Officer</p>
                        <p className="text-sm text-gray-900">{assignment.assignedOfficer}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Culture/Treatment</p>
                        <p className="text-sm text-gray-900">{assignment.culture} - {assignment.treatment}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Replications</p>
                        <p className="text-sm text-gray-900">{assignment.replications}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Location</p>
                        <p className="text-sm text-gray-900">
                          {assignment.latitude}, {assignment.longitude} (Alt: {assignment.altitude}m)
                        </p>
                        <p className="text-sm text-gray-600">{assignment.locationAddress}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Experimental Area</p>
                        <p className="text-sm text-gray-900">{assignment.experimentalArea} ha, Plot Size: {assignment.plotSize} m²</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Soil Type</p>
                        <p className="text-sm text-gray-900">{assignment.soilType}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Irrigation Source</p>
                        <p className="text-sm text-gray-900">{assignment.irrigationSource}</p>
                      </div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => openAssignmentDetailModal(assignment)}
                          className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                        >
                          View Details
                        </button>
                        <button 
                          onClick={() => openEditAssignmentModal(assignment)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Edit Assignment
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                
                {getTrialAssignments(selectedTrial.id).length === 0 && (
                  <div className="text-center py-8">
                    <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No locations assigned to this trial yet.</p>
                    <button
                      onClick={() => {
                        setShowViewModal(false);
                        setShowAssignmentModal(true);
                      }}
                      className="btn-primary mt-4"
                    >
                      Assign First Location
                    </button>
                  </div>
                )}
              </div>

              <div className="flex justify-end pt-4">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Detail View Modal */}
      {showAssignmentDetailModal && selectedAssignment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Assignment Details: {selectedAssignment.locationName}
              </h2>
              <div className="space-y-6">
                {/* Trial Information */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="text-md font-semibold text-blue-900 mb-3">Trial Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Trial Code</p>
                      <p className="text-sm text-gray-900 font-mono">{selectedAssignment.locationCode.substring(0, selectedAssignment.locationCode.length - 3)}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Location Code</p>
                      <p className="text-sm text-gray-900 font-mono">{selectedAssignment.locationCode}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Culture/Treatment</p>
                      <p className="text-sm text-gray-900">{selectedAssignment.culture} - {selectedAssignment.treatment}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Number of Replications</p>
                      <p className="text-sm text-gray-900">{selectedAssignment.replications}</p>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-sm font-medium text-gray-700">Observations</p>
                      <p className="text-sm text-gray-900">{selectedAssignment.observations.join(', ')}</p>
                    </div>
                  </div>
                </div>

                {/* Location Details */}
                <div className="bg-green-50 p-4 rounded-lg">
                  <h3 className="text-md font-semibold text-green-900 mb-3">Location Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Location Name</p>
                      <p className="text-sm text-gray-900">{selectedAssignment.locationName}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Assigned Officer</p>
                      <p className="text-sm text-gray-900">{selectedAssignment.assignedOfficer}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Coordinates</p>
                      <p className="text-sm text-gray-900">{selectedAssignment.latitude}, {selectedAssignment.longitude}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Altitude</p>
                      <p className="text-sm text-gray-900">{selectedAssignment.altitude} m</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">SF Number</p>
                      <p className="text-sm text-gray-900">{selectedAssignment.sfNumber}</p>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-sm font-medium text-gray-700">Address</p>
                      <p className="text-sm text-gray-900">{selectedAssignment.locationAddress}</p>
                    </div>
                  </div>
                </div>










              </div>

              {/* Plot Officer Data Entries */}
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="text-md font-semibold text-purple-900 mb-3">Plot Officer Data Entries</h3>
                <div className="space-y-4">
                  {/* Mock data entries - in real app, this would come from the backend */}
                  <div className="border border-gray-200 rounded-lg p-4 bg-white">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Entry #1 - Germination %</h4>
                        <p className="text-xs text-gray-600">Date: 2024-09-15 | Submitted by: Dr. Rajesh Kumar</p>
                      </div>
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                        Completed
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                      <div>
                        <p className="text-xs font-medium text-gray-700">Culture</p>
                        <p className="text-sm text-gray-900">Hybrid Rice Variety</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-700">Observation</p>
                        <p className="text-sm text-gray-900">Germination %</p>
                      </div>
                    </div>

                    {/* Individual Location Trial Details */}
                    <div className="mb-4">
                      <h5 className="text-xs font-semibold text-gray-700 mb-2">Individual Location Trial Details</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                        <div>
                          <p className="font-medium text-gray-600">Experimental Area</p>
                          <p className="text-gray-900">2.5 ha</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Plot Size</p>
                          <p className="text-gray-900">25 m²</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Soil Type</p>
                          <p className="text-gray-900">Clay Loam</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Soil Test Date</p>
                          <p className="text-gray-900">2024-08-15</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Soil Test Details</p>
                          <p className="text-gray-900">pH: 6.8, N: 280 kg/ha, P: 45 kg/ha, K: 320 kg/ha</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Irrigation Source</p>
                          <p className="text-gray-900">Canal Irrigation</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Previous Crop</p>
                          <p className="text-gray-900">Paddy</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Seed Treatment</p>
                          <p className="text-gray-900">Carbendazim 2g/kg seed</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Ploughing</p>
                          <p className="text-gray-900">2 times disc ploughing</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Sowing Date</p>
                          <p className="text-gray-900">2024-06-15</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Planting Date</p>
                          <p className="text-gray-900">2024-06-20</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Basal Organic</p>
                          <p className="text-gray-900">FYM 5000 kg/ha (2024-06-10)</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Basal Fertilizer</p>
                          <p className="text-gray-900">NPK 20:20:20 120 kg/ha (2024-06-12)</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Top Dressing</p>
                          <p className="text-gray-900">Urea 60 kg/ha (2024-07-15)</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Weed Management</p>
                          <p className="text-gray-900">Hand weeding on 2024-07-20</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Plant Protection</p>
                          <p className="text-gray-900">Chlorpyriphos 2ml/lit on 2024-08-10</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Harvest Date</p>
                          <p className="text-gray-900">2024-10-15</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Monitoring Visit</p>
                          <p className="text-gray-900">2024-09-20 - Good progress observed</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Plot Officer Visit</p>
                          <p className="text-gray-900">2024-09-25 - Field conditions excellent</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Field Day</p>
                          <p className="text-gray-900">2024-10-10</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Inspection Visit</p>
                          <p className="text-gray-900">2024-09-28 - Trial meeting standards</p>
                        </div>
                      </div>
                    </div>

                    <div className="mb-3">
                      <p className="text-xs font-medium text-gray-700 mb-2">Replication Data</p>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="bg-gray-50 p-2 rounded">
                          <p className="font-medium">R1</p>
                          <p>V1: 85.5</p>
                          <p>V2: 87.2</p>
                          <p>V3: 86.8</p>
                        </div>
                        <div className="bg-gray-50 p-2 rounded">
                          <p className="font-medium">R2</p>
                          <p>V1: 88.1</p>
                          <p>V2: 86.9</p>
                          <p>V3: 87.5</p>
                        </div>
                        <div className="bg-gray-50 p-2 rounded">
                          <p className="font-medium">R3</p>
                          <p>V1: 86.3</p>
                          <p>V2: 88.7</p>
                          <p>V3: 87.1</p>
                        </div>
                      </div>
                    </div>

                    <div className="mb-3">
                      <p className="text-xs font-medium text-gray-700 mb-2">Field Photos</p>
                      <div className="flex space-x-2">
                        <div className="w-16 h-16 bg-gray-100 rounded border flex items-center justify-center">
                          <span className="text-xs text-gray-500">Photo 1</span>
                        </div>
                        <div className="w-16 h-16 bg-gray-100 rounded border flex items-center justify-center">
                          <span className="text-xs text-gray-500">Photo 2</span>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs font-medium text-gray-700 mb-1">Farmer Feedback</p>
                        <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                          Crop is showing good germination. Plants are healthy and uniform. No major issues observed.
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-700 mb-1">Officer Assessment</p>
                        <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                          Germination percentage is excellent across all replications. Field conditions are optimal. Monitoring for any pest incidence.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-4 bg-white">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900">Entry #2 - Plant Height</h4>
                        <p className="text-xs text-gray-600">Date: 2024-09-30 | Submitted by: Dr. Rajesh Kumar</p>
                      </div>
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                        Completed
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                      <div>
                        <p className="text-xs font-medium text-gray-700">Culture</p>
                        <p className="text-sm text-gray-900">Hybrid Rice Variety</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-700">Observation</p>
                        <p className="text-sm text-gray-900">Plant Height</p>
                      </div>
                    </div>

                    {/* Individual Location Trial Details */}
                    <div className="mb-4">
                      <h5 className="text-xs font-semibold text-gray-700 mb-2">Individual Location Trial Details</h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                        <div>
                          <p className="font-medium text-gray-600">Experimental Area</p>
                          <p className="text-gray-900">2.5 ha</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Plot Size</p>
                          <p className="text-gray-900">25 m²</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Soil Type</p>
                          <p className="text-gray-900">Clay Loam</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Soil Test Date</p>
                          <p className="text-gray-900">2024-08-15</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Soil Test Details</p>
                          <p className="text-gray-900">pH: 6.8, N: 280 kg/ha, P: 45 kg/ha, K: 320 kg/ha</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Irrigation Source</p>
                          <p className="text-gray-900">Canal Irrigation</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Previous Crop</p>
                          <p className="text-gray-900">Paddy</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Seed Treatment</p>
                          <p className="text-gray-900">Carbendazim 2g/kg seed</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Ploughing</p>
                          <p className="text-gray-900">2 times disc ploughing</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Sowing Date</p>
                          <p className="text-gray-900">2024-06-15</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Planting Date</p>
                          <p className="text-gray-900">2024-06-20</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Basal Organic</p>
                          <p className="text-gray-900">FYM 5000 kg/ha (2024-06-10)</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Basal Fertilizer</p>
                          <p className="text-gray-900">NPK 20:20:20 120 kg/ha (2024-06-12)</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Top Dressing</p>
                          <p className="text-gray-900">Urea 60 kg/ha (2024-07-15)</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Weed Management</p>
                          <p className="text-gray-900">Hand weeding on 2024-07-20</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Plant Protection</p>
                          <p className="text-gray-900">Chlorpyriphos 2ml/lit on 2024-08-10</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Harvest Date</p>
                          <p className="text-gray-900">2024-10-15</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Monitoring Visit</p>
                          <p className="text-gray-900">2024-09-20 - Good progress observed</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Plot Officer Visit</p>
                          <p className="text-gray-900">2024-09-25 - Field conditions excellent</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Field Day</p>
                          <p className="text-gray-900">2024-10-10</p>
                        </div>
                        <div>
                          <p className="font-medium text-gray-600">Inspection Visit</p>
                          <p className="text-gray-900">2024-09-28 - Trial meeting standards</p>
                        </div>
                      </div>
                    </div>

                    <div className="mb-3">
                      <p className="text-xs font-medium text-gray-700 mb-2">Replication Data</p>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="bg-gray-50 p-2 rounded">
                          <p className="font-medium">R1</p>
                          <p>V1: 45.2</p>
                          <p>V2: 46.8</p>
                          <p>V3: 44.9</p>
                        </div>
                        <div className="bg-gray-50 p-2 rounded">
                          <p className="font-medium">R2</p>
                          <p>V1: 47.1</p>
                          <p>V2: 45.6</p>
                          <p>V3: 46.3</p>
                        </div>
                        <div className="bg-gray-50 p-2 rounded">
                          <p className="font-medium">R3</p>
                          <p>V1: 44.8</p>
                          <p>V2: 47.2</p>
                          <p>V3: 45.7</p>
                        </div>
                      </div>
                    </div>

                    <div className="mb-3">
                      <p className="text-xs font-medium text-gray-700 mb-2">Field Photos</p>
                      <div className="flex space-x-2">
                        <div className="w-16 h-16 bg-gray-100 rounded border flex items-center justify-center">
                          <span className="text-xs text-gray-500">Photo 1</span>
                        </div>
                        <div className="w-16 h-16 bg-gray-100 rounded border flex items-center justify-center">
                          <span className="text-xs text-gray-500">Photo 2</span>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs font-medium text-gray-700 mb-1">Farmer Feedback</p>
                        <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                          Plants are growing well with good height. Uniform growth observed across the field.
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-700 mb-1">Officer Assessment</p>
                        <p className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                          Plant height measurements show consistent growth. No lodging observed. Continue monitoring for flowering stage.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="text-center py-4">
                    <p className="text-sm text-gray-600">Total Entries: 2 | Last Updated: 2024-09-30</p>
                  </div>
                </div>
              </div>

            </div>

            <div className="flex justify-end pt-4">
              <button
                onClick={() => setShowAssignmentDetailModal(false)}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrialUpdate; 